import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { ValidationFinding } from '../../types/validation.types';
import FindingSeverityBadge from './FindingSeverityBadge';

interface ValidationFindingsListProps {
  findings: ValidationFinding[];
  showSeverityFilter?: boolean;
}

const ValidationFindingsList: React.FC<ValidationFindingsListProps> = ({
  findings,
  showSeverityFilter = false,
}) => {
  const [filter, setFilter] = React.useState<string>('ALL');

  const filteredFindings = findings.filter(f => {
    if (filter === 'ALL') return true;
    return f.severity === filter;
  });

  const failCount = findings.filter(f => f.severity === 'FAIL').length;
  const warnCount = findings.filter(f => f.severity === 'WARN').length;
  const passCount = findings.filter(f => f.severity === 'PASS').length;

  // Use showSeverityFilter to conditionally render filter chips
  const shouldShowFilters = showSeverityFilter && findings.length > 0;

  if (findings.length === 0) {
    return (
      <Alert severity="info">
        No validation findings available.
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        {shouldShowFilters && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Validation Findings
            </Typography>
            <Stack direction="row" spacing={1}>
            <Chip 
              label={`${failCount} Failures`} 
              color="error" 
              size="small" 
              onClick={() => setFilter('FAIL')}
              variant={filter === 'FAIL' ? 'filled' : 'outlined'}
            />
            <Chip 
              label={`${warnCount} Warnings`} 
              color="warning" 
              size="small"
              onClick={() => setFilter('WARN')}
              variant={filter === 'WARN' ? 'filled' : 'outlined'}
            />
            <Chip 
              label={`${passCount} Passed`} 
              color="success" 
              size="small"
              onClick={() => setFilter('PASS')}
              variant={filter === 'PASS' ? 'filled' : 'outlined'}
            />
            {filter !== 'ALL' && (
              <Chip 
                label="Clear Filter" 
                size="small"
                onClick={() => setFilter('ALL')}
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
        )}

        <Stack spacing={1}>
          {filteredFindings.map((finding) => (
            <Accordion key={finding.id}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <FindingSeverityBadge severity={finding.severity} />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {finding.message}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {finding.ruleKey}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {finding.remediation && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Remediation
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {finding.remediation}
                      </Typography>
                    </Box>
                  )}
                  
                  {finding.evidenceJson && Object.keys(finding.evidenceJson).length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Evidence
                      </Typography>
                      <Box 
                        sx={{ 
                          backgroundColor: '#f5f5f5', 
                          p: 1, 
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          overflow: 'auto',
                        }}
                      >
                        <pre>{JSON.stringify(finding.evidenceJson, null, 2)}</pre>
                      </Box>
                    </Box>
                  )}
                  
                  {finding.tags && finding.tags.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Tags
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {finding.tags.map((tag, idx) => (
                          <Chip key={idx} label={tag} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ValidationFindingsList;
